// src/examples/Sidenav/index.jsx
// -----------------------------------------------------------------------------
// Sidenav (sidebar) avec filtrage par rôles.
// - Lit `routes` (chaque entrée peut définir `allowedRoles: [ "ED", "GA", ... ]`).
// - Trie dynamiquement le MENU selon le rôle courant (user.role).
// - Masque un parent si tous ses enfants sont interdits.
// - Marque un parent comme actif si une de ses sous-routes est active.
//
// ⚠️ La protection RUNTIME des pages est gérée côté routing (App.js)
//    via <PrivateRoute> + (optionnel) <RequireRole>. La sidebar, elle,
//    ne fait qu'afficher/masquer les entrées de menu.
// -----------------------------------------------------------------------------

import PropTypes from "prop-types";
import { useState, useEffect, useMemo } from "react";
import { useLocation, NavLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Tooltip from "@mui/material/Tooltip";

import SidenavRoot from "examples/Sidenav/SidenavRoot";
import MDBox from "components/MDBox";
import { useMaterialUIController, setMiniSidenav } from "context";

// ✅ rôles & auth
import { isRoleAllowed } from "context/roles";
import { useAuth } from "context/AuthContext";

const drawerWidth = 250;
const collapsedWidth = 96;

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const { user } = useAuth(); // ← on lit le rôle courant ici
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});

  // Mini sidenav auto selon la largeur
  useEffect(() => {
    function handleResize() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, setMiniSidenav]);

  // ---------------------------------------------------------------------------
  // Helpers UI / styles
  // ---------------------------------------------------------------------------
  const drawerSx = {
    width: miniSidenav ? collapsedWidth : drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    transition: "width 0.3s ease",
    backgroundColor: darkMode ? "#1e1e2f" : "#ffffff",
    borderRight: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
    zIndex: 1201,
  };

  const navItemSx = (active) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: miniSidenav ? "center" : "flex-start",
    padding: "12px",
    borderRadius: "12px",
    margin: "4px 12px",
    color: active ? "#fff" : darkMode ? "#bbb" : "#1e1e2f",
    background:
      active && darkMode
        ? "linear-gradient(90deg,#3e8ef7 0%,#1e1e2f 100%)"
        : active
          ? "#3e8ef7"
          : "transparent",
    fontWeight: active ? 700 : 400,
    fontSize: "0.875rem",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all 0.2s ease",
  });

  const toggleMenu = (name) => {
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // ---------------------------------------------------------------------------
  // Filtrage par rôle
  // ---------------------------------------------------------------------------
  const userRole = user?.role;

  /** Retourne true si la route simple est active pour l'URL courante. */
  const isRouteActive = (routePath) =>
    routePath && (location.pathname === routePath || location.pathname.startsWith(`${routePath}/`));

  /** Détermine si un item parent doit être marqué comme actif (si un de ses enfants l'est). */
  const isParentActive = (children) =>
    Array.isArray(children) && children.some((c) => isRouteActive(c.route));

  /** Filtre récursivement les routes selon allowedRoles.
   *  - Un parent est gardé si au moins un enfant survit au filtre.
   *  - Les items sans `allowedRoles` sont considérés comme autorisés (fallback permissif).
   */
  const filteredRoutes = useMemo(() => {
    const filterItems = (items) => {
      return items
        .filter((r) => isRoleAllowed(userRole, r.allowedRoles))
        .map((r) => {
          if (r.collapse && Array.isArray(r.collapse)) {
            const filteredChildren = filterItems(r.collapse);
            // Si aucun enfant autorisé → on masque aussi le parent
            if (!filteredChildren.length) return null;
            return { ...r, collapse: filteredChildren };
          }
          return r;
        })
        .filter(Boolean);
    };
    return filterItems(routes || []);
  }, [routes, userRole]);

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------
  return (
    <SidenavRoot
      variant="permanent"
      open
      ownerState={{
        miniSidenav,
        whiteSidenav: false,
        transparentSidenav: false,
        darkMode,
      }}
      sx={drawerSx}
      {...rest}
    >
      {/* Header (logo etc.) optionnel */}
      <MDBox pt={3} px={miniSidenav ? 0 : 2}>
        {filteredRoutes.map(({ type, name, icon, route, collapse }) => {
          // Parent "collapse" avec enfants
          if (type === "collapse" && collapse) {
            const active = isParentActive(collapse) || isRouteActive(route);

            return (
              <Box key={name}>
                {/* Bouton parent (ouvre/ferme le sous-menu) */}
                <Box onClick={() => toggleMenu(name)} sx={navItemSx(active)}>
                  <Tooltip title={name} placement="right" disableHoverListener={!miniSidenav}>
                    {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
                  </Tooltip>
                  {!miniSidenav && (
                    <>
                      <Typography ml={1} fontSize="0.875rem">
                        {name}
                      </Typography>
                      <Box ml="auto">{openMenus[name] ? <ExpandLess /> : <ExpandMore />}</Box>
                    </>
                  )}
                </Box>

                {/* Sous-menu (uniquement les enfants autorisés) */}
                <Collapse in={openMenus[name]} timeout="auto" unmountOnExit>
                  {collapse.map((sub) => {
                    const childActive = isRouteActive(sub.route);
                    return (
                      <NavLink key={sub.name} to={sub.route} style={{ textDecoration: "none" }}>
                        <Box sx={navItemSx(childActive)}>
                          {!miniSidenav && (
                            <Typography ml={4} fontSize="0.8rem">
                              {sub.name}
                            </Typography>
                          )}
                        </Box>
                      </NavLink>
                    );
                  })}
                </Collapse>
              </Box>
            );
          }

          // Item "collapse" simple (entrée de menu directe sans enfants)
          if (type === "collapse") {
            const active = isRouteActive(route);

            return (
              <NavLink key={name} to={route} style={{ textDecoration: "none" }}>
                <Box sx={navItemSx(active)}>
                  <Tooltip title={name} placement="right" disableHoverListener={!miniSidenav}>
                    {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
                  </Tooltip>
                  {!miniSidenav && (
                    <Typography ml={1} fontSize="0.875rem">
                      {name}
                    </Typography>
                  )}
                </Box>
              </NavLink>
            );
          }

          // Les "type: route" ne sont pas rendus dans le menu (pages hors menu)
          return null;
        })}
      </MDBox>
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.node,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
